import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Loader2, Pencil, Trash2, ArrowRight, Search, X, Train as TrainIcon, Repeat, RefreshCw, Bookmark, Clock, ArrowLeftRight } from 'lucide-react';
import { Button } from './ui/button';
import { searchStations, fetchCommute } from '../api/client';
import { getComposition } from '../data/mock';
import ShareTrain from './ShareTrain';
import WhatsAppChannelBadge from './WhatsAppChannelBadge';
import ExportPdfButton from './ExportPdfButton';
import TypeChip from './TypeChip';
import { useConsent } from '../hooks/useConsent';
import { useFluoVehicles } from '../hooks/useFluoVehicles';
import { useGrandEstStations } from '../hooks/useGrandEstStations';

const STORAGE_KEY = 'fluo_commute';

const loadCommute = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const saveCommute = (commute) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(commute)); } catch { /* noop */ }
};

const clearCommute = () => {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
};

/** Inline station autocomplete used by the configurator. */
const StationInput = ({ value, onChange, placeholder, testId }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!query.trim()) { setList([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try { setList(await searchStations(query.trim(), 8)); }
      catch { setList([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={value ? value.name : query}
          onChange={(e) => { onChange(null); setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          data-testid={testId}
          className="w-full h-10 pl-9 pr-9 rounded-md border border-slate-300 focus:border-ter-blue focus:ring-1 focus:ring-ter-blue outline-none text-sm bg-white"
        />
        {value && (
          <button
            type="button"
            onClick={() => { onChange(null); setQuery(''); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 text-slate-400"
            aria-label="clear"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {open && !value && (list.length > 0 || loading) && (
        <ul className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-auto bg-white border border-slate-200 rounded-md shadow-lg">
          {loading && (
            <li className="px-3 py-2 text-xs text-slate-500 flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> …
            </li>
          )}
          {list.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => { onChange({ id: s.id, name: s.name, region: s.region }); setQuery(''); setOpen(false); }}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between gap-2"
              >
                <span className="font-medium text-sm text-slate-800 truncate">{s.name}</span>
                <span className="text-[11px] text-slate-400 truncate">{s.region}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const StatusBadge = ({ status, t }) => {
  const cfg = status === 'cancelled'
    ? { cls: 'bg-ter-red text-white', label: t('board.status_cancelled') }
    : status === 'delayed'
      ? { cls: 'bg-amber-200 text-amber-900', label: t('board.status_delayed') }
      : { cls: 'bg-emerald-100 text-emerald-900', label: t('board.status_ontime') };
  return <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold tracking-wide ${cfg.cls}`}>{cfg.label}</span>;
};

const nowHHMM = () => {
  const d = new Date();
  // Round to next 5-minute mark for friendlier defaults
  const m = Math.ceil(d.getMinutes() / 5) * 5;
  const hh = String((m === 60 ? d.getHours() + 1 : d.getHours()) % 24).padStart(2, '0');
  const mm = String(m === 60 ? 0 : m).padStart(2, '0');
  return `${hh}:${mm}`;
};

const TrainRow = ({ tr, originName, t }) => {
  const { isFluoCar, getFluoCarSeats, getFluoPictoUrl, loaded: fluoLoaded } = useFluoVehicles();
  const { isGrandEst } = useGrandEstStations();
  let comp = tr.status === 'cancelled' ? null : getComposition(tr.number);
  if (comp && fluoLoaded && isFluoCar(tr.number)) {
    comp = { ...comp, totalSeats: getFluoCarSeats(tr.number) };
  }
  const [pictoErr, setPictoErr] = React.useState(false);
  const isFluo = (!tr.type || tr.type === 'TER')
    && (isGrandEst(originName) || isGrandEst(tr.destination || ''));
  const pictoUrl = fluoLoaded && isFluo ? getFluoPictoUrl(tr.number) : null;
  const picto = pictoUrl && !pictoErr
    ? <img src={pictoUrl} className="w-3 h-3 object-contain" alt={isFluoCar(tr.number) ? 'Car Fluo' : 'Train Fluo'} onError={() => setPictoErr(true)} />
    : <TrainIcon className="w-3 h-3 text-ter-blue/60" />;
  const trainDate = tr.datetime
    ? new Date(tr.datetime).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
    : null;
  const today = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  const isTomorrow = trainDate && trainDate !== today;

  return (
    <li className="py-2.5 grid grid-cols-[auto_1fr_auto] gap-x-3 items-center">
      <div className="flex items-center gap-2 min-w-0">
        <div className="flex flex-col items-start">
          <div className="commute-time font-mono font-extrabold text-base text-ter-blue tabular-nums">
            {tr.status === 'cancelled' ? <span className="line-through text-slate-400">{tr.time}</span> : tr.time}
          </div>
          {isTomorrow && (
            <span className="text-xs text-slate-400 block">{trainDate}</span>
          )}
        </div>
        <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
        <div className="commute-destination font-mono font-extrabold text-base text-slate-700 tabular-nums">{tr.arrival_at_dest || '—'}</div>
      </div>
      <div className="min-w-0 flex items-center gap-2 flex-wrap">
        <TypeChip type={tr.type} marque={tr.marque} modeTransport={tr.modeTransport} origin={originName || tr.origin || ''} destination={tr.destination || ''} number={tr.number} />
        <span className="font-mono text-xs text-slate-600">{tr.number}</span>
        {comp && (
          <span className="inline-flex items-center gap-1 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5">
            {picto}
            <span className="font-bold tabular-nums">{comp.wagonsCount}</span>
            <span className="text-slate-400">·</span>
            <span className="font-bold tabular-nums">{comp.totalSeats}</span>
            <span className="text-slate-400">{t('commute.seats_short')}</span>
          </span>
        )}
        {tr.platform && tr.platform !== '—' && (
          <span className="text-[11px] text-slate-500">{t('board.platform_label')} <strong className="text-ter-blue">{tr.platform}</strong></span>
        )}
        {tr.delay > 0 && <span className="text-[11px] text-ter-red font-semibold">+{tr.delay} • {tr.realTime}</span>}
        <StatusBadge status={tr.status} t={t} />
        <WhatsAppChannelBadge station={originName} train={tr} />
      </div>
      <div className="flex items-center gap-1">
        <ShareTrain tab="departures" station={originName} train={tr} />
        {tr.vehicle_journey_id && (
          <Link to={`/train/${encodeURIComponent(tr.vehicle_journey_id)}?number=${encodeURIComponent(tr.number)}&status=${encodeURIComponent(tr.status || 'on_time')}&delay=${encodeURIComponent(tr.delay || 0)}&marque=${encodeURIComponent(tr.marque || '')}&modeTransport=${encodeURIComponent(tr.modeTransport || '')}`}>
            <Button size="icon" variant="ghost" className="h-7 w-7">
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        )}
      </div>
    </li>
  );
};

const TrainsSection = ({ title, fromName, toName, list, loading, error, t }) => (
  <div className="px-4 sm:px-6 py-3">
    <div className="flex items-center gap-2 text-sm mb-2">
      <span className="font-bold text-slate-900">{fromName}</span>
      <ArrowRight className="w-4 h-4 text-ter-red" />
      <span className="font-bold text-slate-900">{toName}</span>
      {title && <span className="text-xs text-slate-500 ml-1">— {title}</span>}
    </div>
    {loading && list.length === 0 && (
      <div className="py-6 text-center text-slate-500 flex items-center justify-center gap-2 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> {t('board.loading')}
      </div>
    )}
    {error && !loading && (
      <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-800">{error}</div>
    )}
    {!loading && !error && list.length === 0 && (
      <div className="py-6 text-center text-sm text-slate-500">{t('commute.empty')}</div>
    )}
    {list.length > 0 && (
      <ol className="divide-y divide-slate-100" data-testid="commute-list">
        {list.map((tr, i) => (
          <TrainRow key={tr.id || i} tr={tr} originName={fromName} t={t} />
        ))}
      </ol>
    )}
  </div>
);

const DailyCommute = () => {
  const { t } = useTranslation();
  const [commute, setCommute] = useState(loadCommute);
  const [editing, setEditing] = useState(!commute);
  const [originDraft, setOriginDraft] = useState(commute ? { id: commute.originId, name: commute.originName } : null);
  const [destDraft, setDestDraft] = useState(commute ? { id: commute.destId, name: commute.destName } : null);
  const { canStorePreferences } = useConsent();
  const [consentError, setConsentError] = useState(false);

  // Settings — persisted with the commute (mode, fromTime, returnEnabled, returnTime)
  const [mode, setMode] = useState(commute?.mode || 'next');
  const [fromTime, setFromTime] = useState(commute?.fromTime || nowHHMM());
  const [returnEnabled, setReturnEnabled] = useState(!!commute?.returnEnabled);
  const [returnTime, setReturnTime] = useState(commute?.returnTime || '17:00');

  const [count, setCount] = useState(() => {
    const saved = localStorage.getItem('commute_count');
    return saved ? parseInt(saved, 10) : 5;
  });

  const [trains, setTrains] = useState([]);
  const [returnTrains, setReturnTrains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingReturn, setLoadingReturn] = useState(false);
  const [error, setError] = useState(null);
  const [returnError, setReturnError] = useState(null);

  useEffect(() => {
    localStorage.setItem('commute_count', count);
  }, [count]);

  const settingsKey = useMemo(
    () => `${mode}|${fromTime}|${returnEnabled ? 1 : 0}|${returnTime}|${count}`,
    [mode, fromTime, returnEnabled, returnTime, count]
  );

  const persistSettings = useCallback(() => {
    if (!commute) return;
    const updated = { ...commute, mode, fromTime, returnEnabled, returnTime };
    saveCommute(updated);
    setCommute(updated);
  }, [commute, mode, fromTime, returnEnabled, returnTime]);

  const load = useCallback(async (c) => {
    if (!c) return;
    setLoading(true);
    setError(null);
    const opts = mode === 'custom'
      ? { fromTime, windowMinutes: 180 }
      : {};
    try {
      const data = await fetchCommute(c.originId, c.destId, count, opts);
      setTrains(data.trains || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'error');
      setTrains([]);
    } finally {
      setLoading(false);
    }
  }, [mode, fromTime, count]);

  const loadReturn = useCallback(async (c) => {
    if (!c || !returnEnabled) {
      setReturnTrains([]);
      return;
    }
    setLoadingReturn(true);
    setReturnError(null);
    const opts = mode === 'custom'
      ? { fromTime: returnTime, windowMinutes: 180 }
      : { fromTime: returnTime, windowMinutes: 180 }; // return always uses a custom time
    try {
      const data = await fetchCommute(c.destId, c.originId, count, opts);
      setReturnTrains(data.trains || []);
    } catch (e) {
      setReturnError(e?.response?.data?.detail || e.message || 'error');
      setReturnTrains([]);
    } finally {
      setLoadingReturn(false);
    }
  }, [returnEnabled, returnTime, mode, count]);

  // Refresh both lists when commute or settings change
  useEffect(() => {
    if (!commute || editing) return;
    load(commute);
    loadReturn(commute);
    const id = setInterval(() => {
      load(commute);
      loadReturn(commute);
    }, 60000);
    return () => clearInterval(id);
  }, [commute, editing, load, loadReturn, settingsKey]);

  const onSave = () => {
    if (!originDraft || !destDraft || originDraft.id === destDraft.id) return;
    // CNIL gating: saving the daily commute writes to localStorage, which is
    // a non-strictly-necessary preference. Without explicit cookie consent we
    // surface a friendly inline message rather than silently dropping the data.
    if (!canStorePreferences) { setConsentError(true); return; }
    setConsentError(false);
    const c = {
      originId: originDraft.id,
      originName: originDraft.name,
      destId: destDraft.id,
      destName: destDraft.name,
      mode,
      fromTime,
      returnEnabled,
      returnTime,
    };
    saveCommute(c);
    setCommute(c);
    setEditing(false);
  };

  const onSwap = () => {
    setOriginDraft(destDraft);
    setDestDraft(originDraft);
  };

  const onDelete = () => {
    clearCommute();
    setCommute(null);
    setOriginDraft(null);
    setDestDraft(null);
    setTrains([]);
    setReturnTrains([]);
    setMode('next');
    setFromTime(nowHHMM());
    setReturnEnabled(false);
    setReturnTime('17:00');
    setEditing(true);
  };

  const onEdit = () => {
    setOriginDraft({ id: commute.originId, name: commute.originName });
    setDestDraft({ id: commute.destId, name: commute.destName });
    setEditing(true);
  };

  const canSave = originDraft && destDraft && originDraft.id !== destDraft.id;

  return (
    <section id="commute" className="max-w-7xl mx-auto px-4 sm:px-6 -mt-8 relative z-10 scroll-mt-20" data-testid="daily-commute">
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200">
        <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-ter-yellow/20 to-transparent border-b border-slate-100 rounded-t-2xl flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-ter-yellow/30 flex items-center justify-center">
              <Bookmark className="w-4 h-4 text-ter-blue" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-ter-red font-bold">{t('commute.eyebrow')}</div>
              <div className="text-base font-extrabold text-ter-blue leading-tight">{t('commute.title')}</div>
            </div>
          </div>
          {commute && !editing && (
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={() => load(commute)} data-testid="commute-refresh">
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> {t('board.refresh')}
              </Button>
              <ExportPdfButton
                commute={commute}
                mode="departures"
                list={trains}
                returnTrains={returnTrains}
                returnEnabled={returnEnabled}
                stationName={commute.originName}
                sourceLabel={t('board.source_live')}
              />
              <Button variant="ghost" size="sm" onClick={onEdit} data-testid="commute-edit">
                <Pencil className="w-3.5 h-3.5 mr-1" /> {t('commute.edit')}
              </Button>
              <Button variant="ghost" size="sm" onClick={onDelete} data-testid="commute-delete" className="text-ter-red hover:text-ter-red">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> {t('commute.delete')}
              </Button>
            </div>
          )}
        </div>

        {editing && (
          <div className="px-4 sm:px-6 py-4 space-y-3">
            <div className="grid sm:grid-cols-[1fr_auto_1fr] gap-3 items-end">
              <div>
                <label className="block text-[11px] tracking-wider text-slate-500 font-semibold mb-1">{t('commute.from')}</label>
                <StationInput value={originDraft} onChange={setOriginDraft} placeholder={t('commute.from_placeholder')} testId="commute-origin-input" />
              </div>
              <button
                type="button"
                onClick={onSwap}
                title={t('commute.swap')}
                data-testid="commute-swap"
                className="self-end h-10 w-10 rounded-md border border-slate-200 hover:border-ter-blue hover:bg-slate-50 transition-colors flex items-center justify-center text-slate-500 hover:text-ter-blue"
              >
                <Repeat className="w-4 h-4" />
              </button>
              <div>
                <label className="block text-[11px] tracking-wider text-slate-500 font-semibold mb-1">{t('commute.to')}</label>
                <StationInput value={destDraft} onChange={setDestDraft} placeholder={t('commute.to_placeholder')} testId="commute-dest-input" />
              </div>
            </div>

            <div className="flex items-end gap-3 flex-wrap pt-1">
              <div className="flex items-center gap-2">
                <span className="text-[11px] tracking-wider text-slate-500 font-semibold">{t('commute.mode_label')}</span>
                <div className="inline-flex rounded-md bg-slate-100 p-0.5" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    onClick={() => setMode('next')}
                    data-testid="commute-edit-mode-next"
                    className={`px-2.5 py-1 text-xs font-bold rounded ${mode === 'next' ? 'bg-white text-ter-blue shadow-sm' : 'text-slate-500'}`}
                  >
                    {t('commute.mode_next')}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    onClick={() => setMode('custom')}
                    data-testid="commute-edit-mode-custom"
                    className={`px-2.5 py-1 text-xs font-bold rounded ${mode === 'custom' ? 'bg-white text-ter-blue shadow-sm' : 'text-slate-500'}`}
                  >
                    {t('commute.mode_custom')}
                  </button>
                </div>
              </div>

              {mode === 'custom' && (
                <label className="flex items-center gap-2 text-xs">
                  <Clock className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-slate-600 font-semibold">{t('commute.from_time_label')}</span>
                  <input
                    type="time"
                    value={fromTime}
                    onChange={(e) => setFromTime(e.target.value)}
                    data-testid="commute-edit-from-time"
                    className="h-8 px-2 rounded-md border border-slate-300 focus:border-ter-blue focus:ring-1 focus:ring-ter-blue outline-none text-sm font-mono"
                  />
                  <span className="text-[11px] text-slate-500">{t('commute.window_3h')}</span>
                </label>
              )}

              <Button onClick={onSave} disabled={!canSave} data-testid="commute-save" className="bg-ter-blue hover:bg-ter-blue-dark text-white h-10 ml-auto">
                {t('commute.save')}
              </Button>
            </div>
            {consentError && (
              <div className="mt-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-[12.5px] text-amber-900 flex items-start gap-2" data-testid="commute-consent-error">
                <span className="font-bold shrink-0">⚠</span>
                <span>{t('commute.cookies_needed')}</span>
              </div>
            )}
          </div>
        )}

        {!editing && commute && (
          <div className="px-4 sm:px-6 py-3 border-b border-slate-100 flex items-end gap-3 flex-wrap" data-testid="commute-settings">
            <div className="flex items-center gap-2 mr-3">
              <span className="text-[11px] tracking-wider text-slate-500 font-semibold">{t('commute.mode_label')}</span>
              <div className="inline-flex rounded-md bg-slate-100 p-0.5" role="tablist">
                <button
                  type="button"
                  role="tab"
                  onClick={() => setMode('next')}
                  data-testid="commute-mode-next"
                  className={`px-2.5 py-1 text-xs font-bold rounded ${mode === 'next' ? 'bg-white text-ter-blue shadow-sm' : 'text-slate-500'}`}
                >
                  {t('commute.mode_next')}
                </button>
                <button
                  type="button"
                  role="tab"
                  onClick={() => setMode('custom')}
                  data-testid="commute-mode-custom"
                  className={`px-2.5 py-1 text-xs font-bold rounded ${mode === 'custom' ? 'bg-white text-ter-blue shadow-sm' : 'text-slate-500'}`}
                >
                  {t('commute.mode_custom')}
                </button>
              </div>
            </div>

            {mode === 'custom' && (
              <label className="flex items-center gap-2 text-xs">
                <Clock className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-slate-600 font-semibold">{t('commute.from_time_label')}</span>
                <input
                  type="time"
                  value={fromTime}
                  onChange={(e) => setFromTime(e.target.value)}
                  data-testid="commute-from-time"
                  className="h-8 px-2 rounded-md border border-slate-300 focus:border-ter-blue focus:ring-1 focus:ring-ter-blue outline-none text-sm font-mono"
                />
                <span className="text-[11px] text-slate-500">{t('commute.window_3h')}</span>
              </label>
            )}

            <div className="ml-auto flex items-center gap-3 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={returnEnabled}
                  onChange={(e) => setReturnEnabled(e.target.checked)}
                  data-testid="commute-return-toggle"
                  className="w-4 h-4 rounded text-ter-blue focus:ring-ter-blue"
                />
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700">
                  <ArrowLeftRight className="w-3.5 h-3.5 text-ter-red" />
                  {t('commute.return_label')}
                </span>
              </label>
              {returnEnabled && (
                <label className="flex items-center gap-2 text-xs">
                  <Clock className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-slate-600 font-semibold">{t('commute.return_from_time')}</span>
                  <input
                    type="time"
                    value={returnTime}
                    onChange={(e) => setReturnTime(e.target.value)}
                    data-testid="commute-return-time"
                    className="h-8 px-2 rounded-md border border-slate-300 focus:border-ter-blue focus:ring-1 focus:ring-ter-blue outline-none text-sm font-mono"
                  />
                  <span className="text-[11px] text-slate-500">{t('commute.window_3h')}</span>
                </label>
              )}
              <select
                value={count}
                onChange={(e) => setCount(Number(e.target.value))}
                data-testid="commute-count-select"
                className="text-sm border border-slate-200 rounded px-2 py-1 bg-white text-slate-600 cursor-pointer hover:border-ter-blue transition-colors h-8"
              >
                <option value={5}>{t('commute.count_label', { n: 5 })}</option>
                <option value={10}>{t('commute.count_label', { n: 10 })}</option>
                <option value={15}>{t('commute.count_label', { n: 15 })}</option>
                <option value={20}>{t('commute.count_label', { n: 20 })}</option>
              </select>
              <Button
                size="sm"
                variant="outline"
                onClick={persistSettings}
                data-testid="commute-persist-settings"
                className="h-8"
              >
                {t('commute.save_settings')}
              </Button>
            </div>
          </div>
        )}

        {!editing && commute && (
          <>
            <TrainsSection
              title={
                mode === 'custom'
                  ? t('commute.custom_window_title', { time: fromTime })
                  : t('commute.next_n', { n: count })
              }
              fromName={commute.originName}
              toName={commute.destName}
              list={trains}
              loading={loading}
              error={error}
              t={t}
            />
            {returnEnabled && (
              <div className="border-t border-slate-100">
                <div className="px-4 sm:px-6 pt-3 text-[11px] uppercase tracking-wider text-ter-red font-bold flex items-center gap-1.5">
                  <ArrowLeftRight className="w-3.5 h-3.5" />
                  {t('commute.return_section')}
                </div>
                <TrainsSection
                  title={t('commute.custom_window_title', { time: returnTime })}
                  fromName={commute.destName}
                  toName={commute.originName}
                  list={returnTrains}
                  loading={loadingReturn}
                  error={returnError}
                  t={t}
                />
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default DailyCommute;
